import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Eye, Minus, Plus, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { Eyebrow, GoldRule, Reveal } from "./Reveal";
import { eur } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Tous", "Kimonos", "Peignoirs", "Capes", "Tabliers", "Staff"];
const SPANS = [
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-7",
];

function QuickView({ product, onClose, onAdd }) {
  const [variant, setVariant] = useState(product.variants[0]);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const images = product.images?.length ? product.images : [product.image];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handlePanEnd = (_, info) => {
    if (images.length < 2) return;
    const { offset, velocity } = info;
    const horizontal =
      Math.abs(offset.x) > Math.abs(offset.y) &&
      (Math.abs(offset.x) > 60 || Math.abs(velocity.x) > 500);
    if (!horizontal) return;
    if (offset.x < 0) setImgIdx((imgIdx + 1) % images.length);
    else setImgIdx((imgIdx - 1 + images.length) % images.length);
  };

  return (
    <motion.div
      data-testid="quick-view-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-[#0C0D0E]/80 backdrop-blur-md"
      data-lenis-prevent
      onClick={onClose}
    >
      <div className="flex min-h-full items-start md:items-center justify-center p-4 sm:p-8">
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.98 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative grid md:grid-cols-2 w-full max-w-4xl overflow-hidden rounded-2xl bg-[#141619] border border-[#E2DBCB]/15"
      >
        <button
          data-testid="quick-view-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#0C0D0E]/70 text-[#F7F5F0] hover:text-[#D4AF37] transition-colors"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>
        <motion.div
          onPanEnd={handlePanEnd}
          className="relative aspect-square touch-pan-y md:aspect-auto md:min-h-[520px] overflow-hidden"
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={images[imgIdx]}
              src={images[imgIdx]}
              alt={`${product.name} — photo ${imgIdx + 1}`}
              data-testid="quick-view-image"
              onClick={() => setZoomed(true)}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 h-full w-full cursor-zoom-in object-cover"
            />
          </AnimatePresence>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0C0D0E]/50 to-transparent md:bg-gradient-to-r" />
          {images.length > 1 && (
            <>
              <button
                data-testid="quick-view-prev"
                onClick={() =>
                  setImgIdx((imgIdx - 1 + images.length) % images.length)
                }
                className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#0C0D0E]/60 text-[#F7F5F0] backdrop-blur-sm transition-colors hover:text-[#D4AF37]"
                aria-label="Photo précédente"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                data-testid="quick-view-next"
                onClick={() => setImgIdx((imgIdx + 1) % images.length)}
                className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#0C0D0E]/60 text-[#F7F5F0] backdrop-blur-sm transition-colors hover:text-[#D4AF37]"
                aria-label="Photo suivante"
              >
                <ChevronRight size={16} />
              </button>
              <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`quick-view-dot-${i}`}
                    onClick={() => setImgIdx(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === imgIdx
                        ? "w-5 bg-[#D4AF37]"
                        : "w-1.5 bg-[#F7F5F0]/40 hover:bg-[#F7F5F0]/70"
                    }`}
                    aria-label={`Photo ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </motion.div>
        <div className="p-8 sm:p-10 flex flex-col">
          <span className="inline-flex w-fit items-center rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">
            {product.tag}
          </span>
          <h3 className="mt-4 font-serif text-2xl sm:text-3xl font-medium leading-tight">
            {product.name}
          </h3>
          <p className="mt-1 text-xs font-mono uppercase tracking-[0.2em] text-[#75726B]">
            {product.category}
          </p>
          <p className="mt-5 text-sm leading-relaxed text-[#B0ABA0]">
            {product.description}
          </p>
          <ul className="mt-5 space-y-2">
            {product.details.map((d) => (
              <li key={d} className="flex items-start gap-3 text-sm text-[#B0ABA0]">
                <span className="mt-2 h-1 w-1 rotate-45 shrink-0 bg-[#D4AF37]" />
                {d}
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#75726B]">
              Déclinaison
            </p>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v) => (
                <button
                  key={v.label}
                  data-testid={`quick-view-variant-${v.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => setVariant(v)}
                  className={`rounded-full border px-4 py-2 text-xs transition-colors ${
                    variant.label === v.label
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "border-[#E2DBCB]/15 text-[#B0ABA0] hover:border-[#D4AF37]/40"
                  }`}
                >
                  {v.label} — {eur(v.price)}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-auto pt-8 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 rounded-full border border-[#E2DBCB]/15 px-2 py-1.5">
              <button
                data-testid="quick-view-qty-minus"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="flex h-7 w-7 items-center justify-center rounded-full text-[#B0ABA0] hover:text-[#D4AF37]"
                aria-label="Réduire la quantité"
              >
                <Minus size={14} />
              </button>
              <span data-testid="quick-view-qty" className="w-6 text-center text-sm">
                {qty}
              </span>
              <button
                data-testid="quick-view-qty-plus"
                onClick={() => setQty(qty + 1)}
                className="flex h-7 w-7 items-center justify-center rounded-full text-[#B0ABA0] hover:text-[#D4AF37]"
                aria-label="Augmenter la quantité"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="text-right">
              <p className="font-serif text-2xl text-[#D4AF37]">{eur(variant.price)}</p>
            </div>
          </div>
          <button
            data-testid="quick-view-add-btn"
            onClick={() => {
              onAdd(product, variant, qty);
              onClose();
            }}
            className="mt-5 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-3.5 text-sm font-semibold text-[#0C0D0E] transition-colors hover:bg-[#C5A059]"
          >
            <ShoppingBag size={15} />
            Ajouter au panier
          </button>
        </div>
        <AnimatePresence>
          {zoomed && (
            <motion.div
              data-testid="quick-view-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setZoomed(false)}
              className="fixed inset-0 z-[95] flex items-center justify-center bg-[#0C0D0E]/95 p-6 backdrop-blur-md"
            >
              <button
                data-testid="quick-view-lightbox-close"
                onClick={() => setZoomed(false)}
                className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DBCB]/20 text-[#F7F5F0] transition-colors hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
              <motion.img
                src={images[imgIdx]}
                alt={`${product.name} — photo ${imgIdx + 1} en grand`}
                initial={{ scale: 0.92, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 10 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[88vh] max-w-full rounded-2xl border border-[#E2DBCB]/15 object-contain"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      </div>
    </motion.div>
  );
}

export default function Collection({ onAdd }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("Tous");
  const [quickView, setQuickView] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch(() =>
        toast.error("Collection indisponible", {
          description: "Réessayez dans un instant.",
        })
      )
      .finally(() => setLoading(false));
  }, []);

  const visible =
    category === "Tous"
      ? products
      : products.filter((p) => p.category === category);

  return (
    <section
      id="collection"
      data-testid="collection-section"
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto"
    >
      <Reveal>
        <Eyebrow>La Collection</Eyebrow>
        <div className="flex flex-wrap items-end justify-between gap-8">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight max-w-xl">
            Des pièces d'exception,{" "}
            <em className="italic text-[#D4AF37]">pensées pour le salon</em>.
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-pill-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`rounded-full border px-5 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                  category === c
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-[#E2DBCB]/15 text-[#B0ABA0] hover:border-[#D4AF37]/40 hover:text-[#F7F5F0]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <GoldRule className="mt-8" />
      </Reveal>

      {loading ? (
        <p
          data-testid="collection-loading"
          className="mt-20 text-center text-sm font-mono uppercase tracking-[0.25em] text-[#75726B]"
        >
          Chargement de la collection…
        </p>
      ) : (
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-10">
          {visible.map((p, i) => (
            <Reveal
              key={p.id}
              delay={(i % 2) * 0.12}
              className={SPANS[i % SPANS.length]}
            >
              <motion.article
                data-testid={`product-card-${p.id}`}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
                className="group relative h-full overflow-hidden rounded-2xl bg-[#141619] border border-[#E2DBCB]/10 transition-colors duration-300 hover:border-[#D4AF37]/40"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-[#0C0D0E]">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C0D0E]/60 via-transparent to-transparent" />
                  <span className="absolute left-5 top-5 inline-flex items-center rounded-full border border-[#D4AF37]/30 bg-[#0C0D0E]/60 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] backdrop-blur-sm">
                    {p.tag}
                  </span>
                  <button
                    data-testid={`quick-view-${p.id}`}
                    onClick={() => setQuickView(p)}
                    className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#0C0D0E]/60 text-[#F7F5F0] opacity-100 backdrop-blur-sm transition-all duration-300 hover:text-[#D4AF37] md:opacity-0 md:group-hover:opacity-100"
                    aria-label={`Aperçu de ${p.name}`}
                  >
                    <Eye size={16} />
                  </button>
                </div>
                <div className="p-6 sm:p-7">
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#75726B]">
                    {p.category}
                  </p>
                  <h3 className="mt-2 font-serif text-xl sm:text-2xl font-medium leading-snug">
                    {p.name}
                  </h3>
                  <p className="mt-3 line-clamp-2 text-sm text-[#B0ABA0]">
                    {p.description}
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <p
                      data-testid={`product-price-${p.id}`}
                      className="font-serif text-xl text-[#D4AF37]"
                    >
                      dès {eur(Math.min(...p.variants.map((v) => v.price)))}
                    </p>
                    <div className="flex gap-2">
                      <button
                        data-testid={`details-${p.id}`}
                        onClick={() => setQuickView(p)}
                        className="rounded-full border border-[#E2DBCB]/15 px-4 py-2 text-xs text-[#B0ABA0] transition-colors hover:border-[#D4AF37]/40 hover:text-[#F7F5F0]"
                      >
                        Détails
                      </button>
                      <button
                        data-testid={`add-to-cart-${p.id}`}
                        onClick={() => onAdd(p, p.variants[0])}
                        className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-semibold text-[#0C0D0E] transition-colors hover:bg-[#C5A059]"
                      >
                        <ShoppingBag size={13} />
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>
      )}

      <AnimatePresence>
        {quickView && (
          <QuickView
            key={quickView.id}
            product={quickView}
            onClose={() => setQuickView(null)}
            onAdd={onAdd}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
